import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { analyticsService } from '../../lib/analytics/analyticsService';
import { reviewClient } from '../../lib/review/reviewClient';
import type { ReviewFormData, ReviewLink } from '../../types/review';
import type { SupportedLanguages } from '../../i18n';
import { logger } from '../../lib/logger';
import { RatingStars } from './RatingStars';
import { PlatformButton, getPlatformIcon, getPlatformName } from './PlatformButton';
import { Toast } from '../shared/Toast';
import { Check, AlertCircle } from 'lucide-react';
import ReviewClient from './ReviewClient';

interface ReviewFormProps {
  config: any;
  translations: Record<string, Record<string, string>>;
  reviewTranslation: string;
  reviewLinks: ReviewLink[];
  sectionId?: string;
}

const ReviewForm: React.FC<ReviewFormProps> = ({
  config,
  translations,
  reviewTranslation,
  reviewLinks,
  sectionId
}) => {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language as SupportedLanguages;
  const { sendEmailNotification } = ReviewClient({ config, translations });

  // Form state
  const [formData, setFormData] = useState<ReviewFormData>({
    name: '',
    comment: '',
    rating: 0
  });
  const [formErrors, setFormErrors] = useState<Partial<ReviewFormData>>({});
  const [hoveredRating, setHoveredRating] = useState<number | null>(null);
  const [submittedPlatforms, setSubmittedPlatforms] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState<{
    show: boolean;
    message: string;
    type: 'success' | 'error';
  }>({
    show: false,
    message: '',
    type: 'success'
  });

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user types
    if (formErrors[name as keyof ReviewFormData]) {
      setFormErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  // Handle rating selection
  const handleRatingClick = (rating: number) => {
    setFormData(prev => ({ ...prev, rating }));
    
    // Clear error when user selects rating
    if (formErrors.rating) {
      setFormErrors(prev => ({ ...prev, rating: undefined }));
    }

    // Track rating selection
    analyticsService.trackEvent({
      event_type: 'interaction',
      event_name: 'review_rating_select',
      event_data: {
        section_id: sectionId,
        rating
      }
    });
  };

  // Handle rating hover
  const handleRatingHover = (rating: number | null) => {
    setHoveredRating(rating);
  };

  // Validate form
  const validateForm = (): boolean => {
    const errors: Partial<ReviewFormData> = {};
    
    if (!formData.name.trim()) {
      errors.name = getTranslatedText('review_name_required') || 'Le nom est obligatoire';
    }
    
    if (!formData.comment.trim()) {
      errors.comment = getTranslatedText('review_comment_required') || 'Le commentaire est obligatoire';
    }
    
    if (formData.rating === 0) {
      errors.rating = getTranslatedText('review_rating_required') || 'La note est obligatoire';
    }
    
    setFormErrors(errors);
    
    if (Object.keys(errors).length > 0) {
      // Track validation errors
      analyticsService.trackEvent({
        event_type: 'error',
        event_name: 'review_validation_error',
        event_data: {
          section_id: sectionId,
          errors: Object.keys(errors)
        }
      });
    }
    
    return Object.keys(errors).length === 0;
  };

  // Handle platform button click
  const handlePlatformClick = async (platform: ReviewLink) => {
    // Validate form first
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Check if already submitted for this platform
      if (submittedPlatforms.has(platform.id)) {
        logger.warn('Review already submitted for this platform', {
          category: 'Review',
          data: { platformId: platform.id, platform: platform.platform }
        });
        
        setToast({
          show: true,
          message: getTranslatedText('review_already_submitted') || 'Vous avez déjà soumis un avis sur cette plateforme',
          type: 'error'
        });
        
        return;
      }

      // Track review submission
      analyticsService.trackEvent({
        event_type: 'interaction',
        event_name: 'review_submit',
        event_data: {
          section_id: sectionId,
          platform: platform.platform,
          rating: formData.rating
        }
      });

      // Create review object
      const review = {
        hotel_id: '',  // Will be set by the client
        section_id: sectionId,
        name: formData.name,
        comment: formData.comment,
        rating: formData.rating,
        language: currentLanguage,
        platform: platform.platform,
        session_id: ''  // Will be set by the client
      };

      // Submit review to database
      const success = await reviewClient.submitReview(review);

      if (success) {
        // Send email notification
        await sendEmailNotification(review);
        
        // Mark platform as submitted
        setSubmittedPlatforms(prev => new Set([...prev, platform.id]));

        // Copy comment to clipboard for easy pasting
        await navigator.clipboard.writeText(formData.comment);

        // Show success toast
        setToast({
          show: true,
          message: getTranslatedText('review_submitted') || 'Votre avis a été soumis avec succès',
          type: 'success'
        });

        // Process URL for website platform
        let targetUrl = platform.url;
        if (platform.platform === 'website') {
          // Replace placeholders in URL
          targetUrl = targetUrl
            .replace(/\{name\}/g, encodeURIComponent(formData.name))
            .replace(/\{comment\}/g, encodeURIComponent(formData.comment))
            .replace(/\{rating\}/g, formData.rating.toString());
        }

        // Open platform URL
        window.open(targetUrl, '_blank', 'noopener,noreferrer');
      } else {
        throw new Error('Failed to submit review');
      }
    } catch (error) {
      logger.error('Error submitting review', {
        category: 'Review',
        data: { error, platform: platform.platform }
      });
      
      // Track error
      analyticsService.trackEvent({
        event_type: 'error',
        event_name: 'review_submit_error',
        event_data: {
          section_id: sectionId,
          platform: platform.platform,
          error_message: error instanceof Error ? error.message : 'Unknown error'
        }
      });
      
      // Show error toast
      setToast({
        show: true,
        message: getTranslatedText('review_submit_error') || 'Une erreur est survenue lors de la soumission de votre avis',
        type: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get translated text from general translations
  const getTranslatedText = (key: string): string => {
    const translation = translations?.[key];
    if (!translation) return key;
    return translation[currentLanguage] || translation.fr || key;
  };

  return (
    <div className="max-w-lg mx-auto px-4 pt-16 pb-8">
      {/* Review form */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8" style={{ borderRadius: config.border_radius }}>
        {/* Introduction text */}
        {reviewTranslation && (
          <div 
            className="mb-6 text-left text-sm"
            dangerouslySetInnerHTML={{ __html: reviewTranslation }}
          />
        )}
        
        {/* Name input */}
        <div className="mb-6">
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            {getTranslatedText('review_name') || 'Votre nom'}
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            className={`w-full px-3 py-2 border rounded-md ${formErrors.name ? 'border-red-500' : 'border-gray-300'}`}
            style={{ borderRadius: config.border_radius }}
            placeholder={getTranslatedText('review_name_placeholder') || 'Entrez votre nom'}
            disabled={isSubmitting}
          />
          {formErrors.name && (
            <p className="mt-1 text-sm text-red-500">{formErrors.name}</p>
          )}
        </div>

        {/* Rating */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {getTranslatedText('review_rating') || 'Votre note'}
          </label>
          <RatingStars
            rating={formData.rating}
            hoveredRating={hoveredRating}
            onRatingClick={handleRatingClick}
            onRatingHover={handleRatingHover}
            color={config['major-color']}
            disabled={isSubmitting}
          />
          {formErrors.rating && (
            <p className="mt-1 text-sm text-red-500">{formErrors.rating}</p>
          )}
        </div>

        {/* Comment textarea */}
        <div className="mb-6">
          <label htmlFor="comment" className="block text-sm font-medium text-gray-700 mb-1">
            {getTranslatedText('review_comment') || 'Votre commentaire'}
          </label>
          <textarea
            id="comment"
            name="comment"
            value={formData.comment}
            onChange={handleInputChange}
            rows={4}
            className={`w-full px-3 py-2 border rounded-md ${formErrors.comment ? 'border-red-500' : 'border-gray-300'}`}
            style={{ borderRadius: config.border_radius }}
            placeholder={getTranslatedText('review_comment_placeholder') || 'Partagez votre expérience...'}
            disabled={isSubmitting}
          />
          {formErrors.comment && (
            <p className="mt-1 text-sm text-red-500">{formErrors.comment}</p>
          )}
        </div>

        {/* Comment baseline */}
        <p className="text-xs text-gray-500 mb-6">
          {getTranslatedText('review_baseline_comment') || 'Votre commentaire sera copié dans le presse-papiers pour faciliter le partage.'}
        </p>

        {/* Platform buttons */}
        <div className="grid grid-cols-2 gap-4">
          {reviewLinks.map((platform) => (
            <PlatformButton
              key={platform.id}
              platform={platform}
              onClick={() => handlePlatformClick(platform)}
              disabled={isSubmitting || submittedPlatforms.has(platform.id)}
              config={config}
            />
          ))}
        </div>
      </div>

      {/* Toast notification */}
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(prev => ({ ...prev, show: false }))}
          duration={3000}
          icon={toast.type === 'success' ? <Check size={18} className="text-white" /> : <AlertCircle size={18} className="text-white" />}
          config={config}
        />
      )}
    </div>
  );
};

export default ReviewForm;